// src/components/das-application/DasApplicationBlankPage.js
import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  memo,
} from "react";

import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetDasStep1Query,
  useSaveDasStep1Mutation,
  useGetDasStep2Query,
  useSaveDasStep2Mutation,
  useGetDasStep3Query,
  useSaveDasStep3Mutation,
  useGetDasStep4Query,
  useGetDasStep5Query,
  useSaveDasStep6Mutation,
} from "../../services/das-app";

import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { SettingsContext } from "../../context/SettingsContext";
import { getSteps } from "./das-application-steps/index";
import { dasStepValidators } from "./dasValidators";
import isEqual from "lodash/isEqual";

import PDFPreviewModal from "./PDFPreviewModal";
import ConfirmSubmitDialog from "../helpers/ConfirmSubmitDialog";
import ErrorSummary from "../helpers/ErrorSummary";
import WarningSummary from "../helpers/WarningSummary";

function DasApplicationBlankPage({
  isSaveChanges,
  onSaveComplete,
  isDirty,
  setIsDirty,
  onStatusChange,
  overrideUser,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  // eslint-disable-next-line
  const { user, genAppLogoutAuthContext, updateUser, isDasAppOverrideActive } =
    useContext(AuthContext);
  const { isDASAppOpen } = useContext(SettingsContext);
  const effectiveUser = overrideUser || user;
  const appID = effectiveUser?.id;
  const canUserEdit =
    isDasAppOverrideActive ||
    (isDASAppOpen && user?.das_application_status !== "Submitted");

  const isReadOnly = !canUserEdit;

  const { data: step1DataResult, isSuccess: step1Loaded } = useGetDasStep1Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step2DataResult, isSuccess: step2Loaded } = useGetDasStep2Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step3DataResult, isSuccess: step3Loaded } = useGetDasStep3Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step4DataResult, isSuccess: step4Loaded } = useGetDasStep4Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step5DataResult, isSuccess: step5Loaded } = useGetDasStep5Query(
    appID,
    {
      skip: !appID,
    }
  );

  const [saveStep1, { isLoading: isSavingStep1 }] = useSaveDasStep1Mutation();
  const [saveStep2, { isLoading: isSavingStep2 }] = useSaveDasStep2Mutation();
  const [saveStep3, { isLoading: isSavingStep3 }] = useSaveDasStep3Mutation();
  const [saveStep6, { isLoading: isSavingStep6 }] = useSaveDasStep6Mutation();

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const initialStepParam = parseInt(queryParams.get("dasStep"), 10);
  const initialStep = Number.isNaN(initialStepParam) ? 0 : initialStepParam;
  const [activeStep, setActiveStep] = useState(initialStep);

  const [errors, setErrors] = useState(Array(6).fill({}));
  const [warnings, setWarnings] = useState(Array(6).fill(null));

  const [formData, setFormData] = useState({
    // Step 1 fields
    legal_name: "",
    citizenship: "Canadian Citizen",
    SIN: "",
    school_id: "",
    CTC: false,
    address: "",
    city: "",
    province: "",
    postalCode: "",
    country: "Canada",
    cellPhone: "",
    dob: "",
    PEN: "",
    // Step 2 fields
    direct_to_post_secondary: false,
    programs_applying_for: "",
    long_term_career_plans: "",
    scholarship_category: "",
    // Step 3 fields
    achievements: "",
    // Step 4 counts
    coursesCount: 0,
    // Step 5 counts
    referencesCount: 0,
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  const steps = getSteps();

  const checkStepWarnings = useCallback((stepIndex, formData) => {
    const stepWarnings = {};
    switch (stepIndex) {
      case 0:
        // No warnings for Step 1
        break;
      case 1:
        if (
          !formData.direct_to_post_secondary &&
          (formData.programs_applying_for.trim() === "" ||
            formData.long_term_career_plans.trim() === "" ||
            formData.scholarship_category.trim() === "")
        ) {
          stepWarnings.missingFields =
            "Consider providing details for programs you're applying for, your long-term career plans, or selecting a scholarship category.";
        }
        break;
      case 2:
        // No specific warnings; add if needed
        break;
      case 3:
        if (formData.coursesCount < 1) {
          stepWarnings.coursesCount =
            "At least one course must be added. Consider adding more courses to strengthen your application.";
        }
        break;
      case 4:
        if (formData.referencesCount < 2) {
          stepWarnings.referencesCount =
            "At least two references are required. Consider adding more references to support your application.";
        }
        break;
      case 5:
        // No specific warnings
        break;
      default:
        break;
    }
    return stepWarnings;
  }, []);

  const validateStep = useCallback(
    (stepIndex) => {
      let stepLoaded = false;
      switch (stepIndex) {
        case 0:
          stepLoaded = step1Loaded;
          break;
        case 1:
          stepLoaded = step2Loaded;
          break;
        case 2:
          stepLoaded = step3Loaded;
          break;
        case 3:
          stepLoaded = step4Loaded;
          break;
        case 4:
          stepLoaded = step5Loaded;
          break;
        case 5:
          stepLoaded = true;
          break;
        default:
          stepLoaded = true;
      }

      if (!stepLoaded) {
        return true;
      }

      const validatorFn = dasStepValidators[stepIndex];
      if (!validatorFn) {
        return true;
      }
      const stepErrs = validatorFn(formData);

      setErrors((prevErrors) => {
        const newErrors = [...prevErrors];
        newErrors[stepIndex] = stepErrs;
        if (!isEqual(newErrors[stepIndex], prevErrors[stepIndex])) {
          return newErrors;
        }
        return prevErrors;
      });

      const stepWarnings = checkStepWarnings(stepIndex, formData);

      setWarnings((prevWarnings) => {
        const newWarnings = [...prevWarnings];
        newWarnings[stepIndex] =
          stepWarnings && Object.keys(stepWarnings).length > 0
            ? stepWarnings
            : null;
        if (!isEqual(newWarnings[stepIndex], prevWarnings[stepIndex])) {
          return newWarnings;
        }
        return prevWarnings;
      });

      return Object.keys(stepErrs).length === 0;
    },
    [
      step1Loaded,
      step2Loaded,
      step3Loaded,
      step4Loaded,
      step5Loaded,
      formData,
      checkStepWarnings,
    ]
  );

  const handleChange = (field) => (e) => {
    const target = e.target;
    let value;

    if (target.type === "checkbox") {
      value = target.checked;
    } else if (target.type === "radio") {
      if (field === "direct_to_post_secondary") {
        value = target.value === "Yes" ? true : false;
      } else {
        value = target.value;
      }
    } else if (field === "school_id") {
      value = target.value ? String(target.value) : "";
    } else if (target.type === "number") {
      value = target.value === "" ? "" : Number(target.value);
    } else {
      value = target.value;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    validateStep(activeStep);
  };

  const updateCounts = () => {
    const courses =
      step4DataResult?.data.filter((entry) => entry.Type === "Course") || [];
    const references = step5DataResult?.data || [];

    setFormData((prev) => ({
      ...prev,
      coursesCount: courses.length,
      referencesCount: references.length,
    }));
  };

  const getErrorSteps = () => {
    const errorStepIndices = [];
    errors?.forEach((stepErr, index) => {
      if (stepErr && Object.keys(stepErr).length > 0) {
        errorStepIndices.push(index);
      }
    });
    warnings?.forEach((warningObj, index) => {
      if (warningObj && Object.keys(warningObj).length > 0) {
        if (!errorStepIndices.includes(index)) {
          errorStepIndices.push(index);
        }
      }
    });

    return errorStepIndices;
  };

  useEffect(() => {
    if (step1Loaded && step1DataResult?.success) {
      const step1Data = step1DataResult.data;
      setFormData((prev) => ({
        ...prev,
        // Step 1 fields:
        legal_name: step1Data.legal_name || "",
        citizenship: step1Data.citizenship || "Canadian Citizen",
        SIN: step1Data.SIN || "",
        school_id: step1Data.school_id ? String(step1Data.school_id) : "",
        CTC: !!step1Data.CTC,
        address: step1Data.address || "",
        city: step1Data.city || "",
        province: step1Data.province || "",
        postalCode: step1Data.postalCode || "",
        country: step1Data.country || "Canada",
        cellPhone: step1Data.cellPhone || "",
        dob: step1Data.dob || "",
        PEN: step1Data.PEN || "",
      }));
    }
  }, [activeStep, step1Loaded, step1DataResult]);

  useEffect(() => {
    if (activeStep === 0 && step1Loaded && step1DataResult?.success) {
      validateStep(0);
    }
    // eslint-disable-next-line
  }, [formData, activeStep]);

  useEffect(() => {
    if (step2Loaded && step2DataResult?.success) {
      const step2Data = step2DataResult.data;
      setFormData((prev) => ({
        ...prev,
        // Step 2 fields:
        direct_to_post_secondary: step2Data.direct_to_post_secondary || false,
        programs_applying_for: step2Data.programs_applying_for || "",
        long_term_career_plans: step2Data.long_term_career_plans || "",
        scholarship_category: step2Data.scholarship_category || "",
      }));
    }
  }, [activeStep, step2Loaded, step2DataResult]);

  useEffect(() => {
    if (activeStep === 1 && step2Loaded && step2DataResult?.success) {
      validateStep(1);
    }
    // eslint-disable-next-line
  }, [formData, activeStep]);

  useEffect(() => {
    if (step3Loaded && step3DataResult?.success) {
      const step3Data = step3DataResult.data;

      setFormData((prev) => ({
        ...prev,
        // Step 3 fields:
        achievements: step3Data.achievements || "",
      }));
    }
  }, [activeStep, step3Loaded, step3DataResult]);

  useEffect(() => {
    if (activeStep === 2 && step3Loaded && step3DataResult?.success) {
      validateStep(2);
    }
    // eslint-disable-next-line
  }, [formData, activeStep]);

  useEffect(() => {
    if (step4Loaded && step4DataResult?.success) {
      updateCounts();
    } // eslint-disable-next-line
  }, [activeStep, step4Loaded, step4DataResult]);

  useEffect(() => {
    if (activeStep === 3 && step4Loaded && step4DataResult?.success) {
      validateStep(3);
    }
    // eslint-disable-next-line
  }, [formData, activeStep]);

  useEffect(() => {
    if (step5Loaded && step5DataResult?.success) {
      updateCounts();
    } // eslint-disable-next-line
  }, [activeStep, step5Loaded, step5DataResult]);

  useEffect(() => {
    if (activeStep === 4 && step5Loaded && step5DataResult?.success) {
      validateStep(4);
    }
    // eslint-disable-next-line
  }, [formData, activeStep]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (String(activeStep) !== params.get("dasStep")) {
      params.set("dasStep", String(activeStep));
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [activeStep, location.search, location.pathname, navigate]);

  useEffect(() => {
    const storedFormData = localStorage.getItem("dasFormData");
    if (storedFormData) {
      const parsed = JSON.parse(storedFormData);
      const stillEmpty = Object.values(formData).every(
        (val) => val === "" || val === false
      );
      if (stillEmpty) {
        setFormData(parsed);
      }
    }
    const allStepsLoaded =
      step1Loaded && step2Loaded && step3Loaded && step4Loaded && step5Loaded;

    if (allStepsLoaded) {
      setIsDataLoaded(true);
    } // eslint-disable-next-line
  }, [step1Loaded, step2Loaded, step3Loaded, step4Loaded, step5Loaded]);

  useEffect(() => {
    if (isDataLoaded && initialFormData === null) {
      setInitialFormData(formData);
    }
  }, [isDataLoaded, initialFormData, formData]);

  useEffect(() => {
    if (!isDataLoaded || !initialFormData) return;

    if (!isEqual(formData, initialFormData)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [formData, initialFormData, isDataLoaded, setIsDirty]);

  useEffect(() => {
    localStorage.setItem("dasFormData", JSON.stringify(formData));
  }, [formData]);

  const handleTabChange = async (event, newValue) => {
    if (!isDataLoaded) {
      setActiveStep(newValue);
      return;
    }
    if (initialFormData && !isEqual(formData, initialFormData)) {
      await handleSaveAndContinue(false);
      setInitialFormData(formData);
    }

    validateStep(activeStep);
    setActiveStep(newValue);
  };

  const handleSaveAndContinue = async (finalSubmit = false) => {
    if (!isDataLoaded) return;

    if (!canUserEdit) {
      addNotification(
        "You do not have permission to save changes as the application is closed.",
        "error"
      );
      return;
    }

    if (!appID) {
      addNotification("User not authenticated.", "error");
      return;
    }

    const isStepValid = validateStep(activeStep);
    if (!isStepValid) {
      addNotification(
        "DAS App: Some required fields are still missing or invalid. " +
          "We saved your progress, but you must fix these before final submission.",
        "warning"
      );
    }

    let dataToSave = {};
    let saveFunction = null;

    if (activeStep === 0) {
      // Step 1: Personal Information
      dataToSave = {
        legal_name: formData.legal_name || "",
        citizenship: formData.citizenship || "Canadian Citizen",
        SIN: formData.SIN || "",
        school_id: formData.school_id ? Number(formData.school_id) : "",
        CTC: formData.CTC,
        address: formData.address || "",
        city: formData.city || "",
        province: formData.province || "",
        postalCode: formData.postalCode || "",
        country: formData.country || "Canada",
        cellPhone: formData.cellPhone || "",
        dob: formData.dob || null,
        PEN: formData.PEN || "",
      };
      saveFunction = saveStep1;
    } else if (activeStep === 1) {
      // Step 2: Career and Post-Secondary Plans
      dataToSave = {
        direct_to_post_secondary: formData.direct_to_post_secondary,
        programs_applying_for: formData.programs_applying_for || "",
        long_term_career_plans: formData.long_term_career_plans || "",
        scholarship_category: formData.scholarship_category || "",
      };
      saveFunction = saveStep2;
    } else if (activeStep === 2) {
      // Step 3: Evidence of Achievement
      dataToSave = {
        achievements: formData.achievements || "",
      };
      saveFunction = saveStep3;
    } else if (activeStep === 3 || activeStep === 4) {
      try {
        addNotification("Progress saved successfully.", "success");
        setActiveStep((prev) => prev + 1);
      } catch (err) {
        console.error("Save and Continue Error:", err);
        addNotification("Failed to save progress. Please try again.", "error");
      }
      return;
    } else if (activeStep === 5) {
      // Step 6: Review & Submit
      if (user?.das_application_status === "Submitted") {
        addNotification("Application has already been submitted.", "info");
        return;
      }
      const isValid = validateStep(5);
      if (!isValid) {
        addNotification(
          "DAS App: Please fix errors before submitting.",
          "error"
        );
        return;
      }
      let allValid = true;
      for (let i = 0; i < 6; i++) {
        const stepOk = validateStep(i);
        if (!stepOk) allValid = false;
      }
      if (!allValid) {
        const errorSteps = getErrorSteps();
        const errorStepLabels = errorSteps.map(
          (stepIndex) => steps[stepIndex].label
        );
        addNotification(
          `DAS App: Cannot submit application. Please fix errors in the following steps: ${errorStepLabels.join(
            ", "
          )}.`,
          "error"
        );
        return;
      }

      if (!finalSubmit) {
        addNotification("Progress saved successfully.", "success");
        onSaveComplete();
        return;
      }

      if (finalSubmit) {
        dataToSave = { submitted: true };
      } else {
        dataToSave = {};
      }
      saveFunction = saveStep6;
    }

    if (dataToSave && saveFunction) {
      try {
        const result = await saveFunction({ appID, data: dataToSave }).unwrap();
        if (activeStep === 5 && finalSubmit) {
          if (!overrideUser) {
            updateUser({
              das_application_status: "Submitted",
              dasAccessOverrideUntil: null,
            });
          }

          setFormData((prev) => ({ ...prev, applicationStatus: "Submitted" }));
          addNotification(
            result?.message || "DAS App: Application submitted successfully.",
            "success"
          );
        } else {
          addNotification(
            result?.message || "Progress saved successfully.",
            "success"
          );
          if (activeStep < steps.length - 1) {
            setActiveStep((prev) => prev + 1);
          }
        }
        setInitialFormData(formData);
      } catch (err) {
        console.error("Save and Continue Error:", err);
        addNotification("Failed to save progress. Please try again.", "error");
      }
    }
  };

  const handleSaveAndExit = async (finalSubmit = false) => {
    if (!isDataLoaded) return;

    if (!canUserEdit) {
      addNotification(
        "You do not have permission to save changes as the application is closed.",
        "error"
      );
      return;
    }

    if (!appID) {
      addNotification("User not authenticated.", "error");
      return;
    }

    const isStepValid = validateStep(activeStep);
    if (!isStepValid) {
      addNotification(
        "DAS App: Some required fields are still missing or invalid. " +
          "We saved your progress, but you must fix these before final submission.",
        "warning"
      );
    }

    let dataToSave = {};
    let saveFunction = null;

    if (activeStep === 0) {
      // Step 1: Personal Information
      dataToSave = {
        legal_name: formData.legal_name || "",
        citizenship: formData.citizenship || "Canadian Citizen",
        SIN: formData.SIN || "",
        school_id: formData.school_id ? Number(formData.school_id) : "",
        CTC: formData.CTC,
        address: formData.address || "",
        city: formData.city || "",
        province: formData.province || "",
        postalCode: formData.postalCode || "",
        country: formData.country || "Canada",
        cellPhone: formData.cellPhone || "",
        dob: formData.dob || null,
        PEN: formData.PEN || "",
      };
      saveFunction = saveStep1;
    } else if (activeStep === 1) {
      // Step 2: Career and Post-Secondary Plans
      dataToSave = {
        direct_to_post_secondary: formData.direct_to_post_secondary,
        programs_applying_for: formData.programs_applying_for || "",
        long_term_career_plans: formData.long_term_career_plans || "",
        scholarship_category: formData.scholarship_category || "",
      };
      saveFunction = saveStep2;
    } else if (activeStep === 2) {
      // Step 3: Evidence of Achievement
      dataToSave = {
        achievements: formData.achievements || "",
      };
      saveFunction = saveStep3;
    } else if (activeStep === 3 || activeStep === 4) {
      try {
        addNotification("Progress saved successfully.", "success");
        setActiveStep((prev) => prev + 1);
      } catch (err) {
        console.error("Save and Continue Error:", err);
        addNotification("Failed to save progress. Please try again.", "error");
      }
      return;
    } else if (activeStep === 5) {
      // Step 6: Review & Submit
      if (user?.das_application_status === "Submitted") {
        addNotification("Application has already been submitted.", "info");
        return;
      }

      const isValid = validateStep(5);
      if (!isValid) {
        addNotification(
          "DAS App: Please fix errors before submitting.",
          "error"
        );
        return;
      }
      let allValid = true;
      for (let i = 0; i < 6; i++) {
        const stepOk = validateStep(i);
        if (!stepOk) allValid = false;
      }
      if (!allValid && finalSubmit) {
        const errorSteps = getErrorSteps();
        const errorStepLabels = errorSteps.map(
          (stepIndex) => steps[stepIndex].label
        );
        addNotification(
          `DAS App: Cannot submit application. Please fix errors in the following steps: ${errorStepLabels.join(
            ", "
          )}.`,
          "error"
        );
        return;
      }

      if (!finalSubmit) {
        addNotification("Progress saved successfully.", "success");
        onSaveComplete();
        setTimeout(() => {
          genAppLogoutAuthContext();
        }, 1000);
      }

      dataToSave = {};
      saveFunction = saveStep6;
    }

    if (dataToSave && saveFunction) {
      try {
        const result = await saveFunction({ appID, data: dataToSave }).unwrap();
        if (activeStep === 5 && finalSubmit) {
          if (!overrideUser) {
            updateUser({
              das_application_status: "Submitted",
              dasAccessOverrideUntil: null,
            });
          }
          setFormData((prev) => ({ ...prev, applicationStatus: "Submitted" }));

          addNotification(
            result?.message || "Application submitted successfully.",
            "success"
          );
        } else {
          addNotification(
            result?.message || "Your progress has been saved.",
            "success"
          );
          genAppLogoutAuthContext();
        }
        setInitialFormData(formData);
      } catch (err) {
        console.error("Save and Exit Error:", err);
        addNotification("Failed to save progress. Please try again.", "error");
      }
    }
  };

  const handlePreviewDialogOpen = () => setOpenPreviewDialog(true);
  const handlePreviewDialogClose = () => setOpenPreviewDialog(false);

  useEffect(() => {
    if (activeStep === steps.length - 1 && isDataLoaded) {
      steps.forEach((_, index) => {
        validateStep(index);
      });
    }
  }, [activeStep, isDataLoaded, steps, validateStep]);

  useEffect(() => {
    if (isSaveChanges) {
      if (!isDirty) {
        onSaveComplete();
        return;
      }
      if (initialFormData && !isEqual(formData, initialFormData)) {
        handleSaveAndContinue(false)
          .then(() => {
            onSaveComplete();
            setIsDirty(false);
          })
          .catch((error) => {
            console.error("Save operation failed:", error);
            addNotification(
              "Failed to save progress. Please try again.",
              "error"
            );
            onSaveComplete();
          });
      } else {
        onSaveComplete();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveChanges]);

  useEffect(() => {
    const anyErrors = errors.some(
      (stepErr) => stepErr && Object.keys(stepErr).length > 0
    );
    const anyWarnings = warnings.some(
      (warningObj) => warningObj && Object.keys(warningObj).length > 0
    );

    if (anyErrors) {
      onStatusChange("error");
    } else if (anyWarnings) {
      onStatusChange("warning");
    } else {
      onStatusChange("success");
    }
  }, [errors, warnings, onStatusChange]);

  const isSavingSteps =
    isSavingStep1 || isSavingStep2 || isSavingStep3 || isSavingStep6;

  const hasErrors = errors.some(
    (stepErr) => stepErr && Object.keys(stepErr).length > 0
  );

  return (
    <div className="application-blank-page">
      <Box sx={{ width: "100%", typography: "body1" }}>
        <Tabs
          value={activeStep}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Application Steps"
        >
          {steps?.map((step, index) => {
            const stepErrObj = errors[index] || {};
            const stepWarning = warnings[index] || null;

            const noErrors = Object.keys(stepErrObj).length === 0;
            const hasWarnings = stepWarning;

            let iconToShow;
            let tooltipTitle = "";

            if (!noErrors) {
              iconToShow = <ErrorOutlineIcon color="error" />;
              tooltipTitle = Object.values(stepErrObj).join(" ");
            } else if (hasWarnings) {
              iconToShow = <WarningAmberIcon sx={{ color: "#ffd500" }} />;
              tooltipTitle = Object.values(stepWarning).join(" ");
            } else {
              iconToShow = <CheckCircleIcon color="success" />;
            }

            return (
              <Tooltip
                key={`das-step-${index}`}
                title={tooltipTitle}
                placement="top"
                arrow
                disableHoverListener={!tooltipTitle}
              >
                <Tab
                  icon={iconToShow}
                  iconPosition="start"
                  label={step?.label}
                  sx={{
                    backgroundColor: activeStep === index ? "#3e9d5f" : "#eee",
                    color: activeStep === index ? "#ffffff" : "#000000",
                    "&.Mui-selected": {
                      backgroundColor: "#3e9d5f",
                      color: "#ffffff",
                    },
                    "&:hover": {
                      backgroundColor: "#5DC1AF",
                    },
                    borderRadius: "15px 15px 0 0",
                    border: "1px solid #a3a3a3",
                    textTransform: "none",
                    position: "relative",
                    borderBottom: !noErrors
                      ? "3px solid #f44336"
                      : hasWarnings
                      ? "3px solid #ffd500"
                      : "1px solid #a3a3a3",
                  }}
                />
              </Tooltip>
            );
          })}
        </Tabs>
        {user?.das_application_status === "Submitted" &&
          !isDasAppOverrideActive && (
            <div className="notice-banner">
              Application has been submitted - No Changes permitted
            </div>
          )}
        {!isDASAppOpen &&
          !isDasAppOverrideActive &&
          user?.das_application_status !== "Submitted" && (
            <div className="notice-banner">
              Application period is now closed - No Changes Permitted
            </div>
          )}
        <Box sx={{ p: 2.5, m: 0 }}>
          {activeStep === steps.length - 1 && (
            <>
              <ErrorSummary
                errorSteps={errors
                  .map((stepErr, index) =>
                    stepErr && Object.keys(stepErr).length > 0
                      ? { stepIndex: index, fields: stepErr }
                      : null
                  )
                  .filter((stepErrObj) => stepErrObj !== null)}
                steps={steps}
                onNavigate={(stepIndex) => setActiveStep(stepIndex)}
              />
              <WarningSummary
                warningSteps={warnings
                  .map((warningObj, index) =>
                    warningObj && Object.keys(warningObj).length > 0
                      ? { stepIndex: index, warnings: warningObj }
                      : null
                  )
                  .filter((warningStepObj) => warningStepObj !== null)}
                steps={steps}
                onNavigate={(stepIndex) => setActiveStep(stepIndex)}
              />
            </>
          )}
          {React.cloneElement(steps[activeStep]?.content, {
            formData,
            setFormData,
            stepErrors: errors[activeStep],
            handleChange,
            isReadOnly,
            appID,
          })}
          <Box className="application-blank-page-buttons-group">
            <Button
              onClick={() => handleSaveAndExit(false)}
              variant="contained"
              color="secondary"
              disabled={isSavingSteps || !canUserEdit}
              startIcon={isSavingSteps && <CircularProgress size={20} />}
            >
              {isSavingSteps ? "Saving..." : "Save and Exit"}
            </Button>
            <Button
              onClick={handlePreviewDialogOpen}
              variant="contained"
              color="secondary"
            >
              Preview / PRINT / Download
            </Button>
            {activeStep < steps?.length - 1 ? (
              <Button
                onClick={() => handleSaveAndContinue(false)}
                variant="contained"
                color="primary"
                disabled={isSavingSteps || !canUserEdit}
                startIcon={isSavingSteps && <CircularProgress size={20} />}
              >
                {isSavingSteps ? "Saving..." : "Save and Continue"}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setShowConfirmSubmit(true)}
                  variant="contained"
                  color="success"
                  disabled={isSavingSteps || !canUserEdit || hasErrors}
                  startIcon={isSavingSteps && <CircularProgress size={20} />}
                >
                  {user?.das_application_status === "Submitted"
                    ? "Submitted"
                    : isSavingSteps
                    ? "Submitting..."
                    : "Submit Application"}
                </Button>
                <ConfirmSubmitDialog
                  open={showConfirmSubmit}
                  onClose={() => setShowConfirmSubmit(false)}
                  onConfirm={() => {
                    setShowConfirmSubmit(false);
                    handleSaveAndContinue(true);
                  }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
      <PDFPreviewModal
        open={openPreviewDialog}
        onClose={handlePreviewDialogClose}
        appID={appID}
      />
    </div>
  );
}

export default memo(DasApplicationBlankPage);
